import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  PlusCircle,
  Search,
  FilterIcon,
  Loader2,
  Pencil,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Eye,
  Download,
  FileSpreadsheet,
  X,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';
import DynamicFormModal from '@/components/DynamicFormModal';
import ExportExcelButton from '@/components/ExportExcelButton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const ITEMS_PER_PAGE = 10;

const ParametrizacionInlineTable = ({
  title,
  idLavanderia,
  fetchService,
  columns,
  filterFields,
  onCreate,
  onEdit,
  onDelete,
  onView,
  exportService,
  extraQuery,
  createService,
  updateService,
  deleteService,
  formFields,
  scope = 'centro',
  extraActions = [],
  exportColumns,
}) => {
  const { toast } = useToast();
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({});
  const [showFilters, setShowFilters] = useState(false);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [currentItem, setCurrentItem] = useState(null);

  // Delete dialog state
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  // View detail modal
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [detailItem, setDetailItem] = useState(null);

  const hasCrud = Boolean(createService && updateService && deleteService && formFields);

  // Memoize props that could be inline objects/arrays from parent to avoid infinite loops
  const resolvedExtraQuery = useMemo(() => extraQuery || {}, [extraQuery]);
  const resolvedColumns = useMemo(() => columns || [], [columns]);
  const resolvedFilterFields = useMemo(() => filterFields || [], [filterFields]);
  const resolvedFormFields = useMemo(() => {
    let fields = (formFields || []).filter((f) => f.id !== 'actions' && f.key !== 'actions');
    // If scoped to a centro, hide lavanderia field (it will be auto-injected on save)
    if (scope === 'centro' && idLavanderia) {
      fields = fields.filter((f) => f.id !== 'lavanderia');
    }
    return fields;
  }, [formFields, scope, idLavanderia]);

  const buildQuery = useCallback(() => {
    const query = { ...resolvedExtraQuery };
    if (scope === 'centro' && idLavanderia) {
      query['lavanderia.id'] = idLavanderia;
    }
    if (searchTerm.trim()) {
      const term = searchTerm.trim();
      const searchableCols = resolvedColumns.filter((c) => c.searchable);
      if (searchableCols.length > 0) {
        query.$or = searchableCols.map((c) => ({
          [c.id]: { $regex: term, $options: 'i' },
        }));
      }
    }
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== '' && value !== undefined && value !== null) {
        query[key] = value;
      }
    });
    return query;
  }, [scope, idLavanderia, searchTerm, filters, resolvedExtraQuery, resolvedColumns]);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const query = buildQuery();
      const response = await fetchService({
        limit: ITEMS_PER_PAGE,
        offset: currentPage,
        page: currentPage,
        query,
      });
      const records = response?.data || [];
      const total = response?.totalRecords || records.length;
      setData(records);
      setTotalItems(total);
    } catch (error) {
      console.error('Error loading data:', error);
      toast({ title: 'Error', description: 'No se pudieron cargar los datos', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  }, [buildQuery, currentPage, fetchService, toast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE) || 1;

  const handleFilterChange = (fieldId, value) => {
    setFilters((prev) => ({ ...prev, [fieldId]: value }));
    setCurrentPage(1);
  };

  const handleRemoveFilter = (fieldId) => {
    setFilters((prev) => {
      const next = { ...prev };
      delete next[fieldId];
      return next;
    });
    setCurrentPage(1);
  };

  // CRUD handlers
  const handleOpenCreate = () => {
    if (onCreate && !hasCrud) {
      onCreate();
      return;
    }
    setModalMode('create');
    setCurrentItem(null);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (row) => {
    if (onEdit && !hasCrud) {
      onEdit(row);
      return;
    }
    setModalMode('edit');
    setCurrentItem(row);
    setIsModalOpen(true);
  };

  const handleOpenDelete = (row) => {
    if (onDelete && !hasCrud) {
      onDelete(row);
      return;
    }
    setItemToDelete(row);
    setIsDeleteDialogOpen(true);
  };

  const handleOpenDetail = (row) => {
    setDetailItem(row);
    setIsDetailOpen(true);
  };

  const handleSave = async (formData) => {
    try {
      // Pre-fill lavanderia when creating from a centro page (only for centro-scoped modules)
      const payload = { ...formData };
      if (scope === 'centro' && modalMode === 'create' && idLavanderia) {
        payload.lavanderia = { id: idLavanderia };
      }

      if (modalMode === 'create') {
        await createService(payload);
        toast({ title: 'Éxito', description: 'Registro creado correctamente.' });
      } else {
        await updateService(currentItem._id, payload);
        toast({ title: 'Éxito', description: 'Registro actualizado correctamente.' });
      }
      setIsModalOpen(false);
      setCurrentItem(null);
      loadData();
    } catch (error) {
      console.error('Error saving:', error);
      const msg = error?.response?.data?.message || error?.message || 'Ocurrió un problema al guardar.';
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    }
  };

  const handleConfirmDelete = async () => {
    try {
      if (itemToDelete) {
        const response = await deleteService(itemToDelete._id);
        if (response && (response.code === 200 || response.success || response.status === 200)) {
          toast({ title: 'Éxito', description: 'Registro eliminado correctamente.' });
        } else {
          throw new Error(response?.message || 'Error al eliminar');
        }
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error?.message || 'Ocurrió un problema al eliminar el registro.',
        variant: 'destructive',
      });
    } finally {
      setIsDeleteDialogOpen(false);
      setItemToDelete(null);
      loadData();
    }
  };

  // Helper: resuelve paths con notación de puntos para acceder a campos anidados.
  // Ej: "familia.nombre" sobre { familia: { nombre: "X" } } devuelve "X".
  const getNestedValue = (obj, path) => {
    if (obj == null || path == null) return undefined;
    return String(path).split('.').reduce((acc, key) => {
      return acc != null ? acc[key] : acc;
    }, obj);
  };

  const renderCell = (row, col) => {
    if (col.render) return col.render(row);
    const value = col.accessor ? getNestedValue(row, col.accessor) : row[col.id];
    if (col.type === 'status') {
      const isActive = value === 'Activo' || value === 'Activa';
      return (
        <Badge variant="outline" className={isActive ? 'bg-green-50 text-green-700 border-green-200' : 'bg-gray-50 text-gray-700 border-gray-200'}>
          {value || '-'}
        </Badge>
      );
    }
    if (col.type === 'boolean') {
      return value ? 'Sí' : 'No';
    }
    return value || '-';
  };

  const showActions = Boolean(onView || onEdit || onDelete || hasCrud || extraActions.length > 0);
  const canCreate = Boolean(onCreate || hasCrud);
  const canEdit = Boolean(onEdit || hasCrud);
  const canDelete = Boolean(onDelete || hasCrud);
  const canExport = Boolean(exportService && exportColumns && exportColumns.length > 0);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <p className="text-xs text-gray-500">{totalItems} registro(s) encontrado(s)</p>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <div className="relative flex-1 sm:flex-none">
            <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Buscar..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              className="pl-9 text-sm h-9 w-full sm:w-64"
            />
          </div>
          {resolvedFilterFields.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters((prev) => !prev)}
              className="gap-1.5"
            >
              <FilterIcon className="h-3.5 w-3.5" />
              Filtros
            </Button>
          )}
          {canExport && (
            <ExportExcelButton
              service={exportService}
              filters={buildQuery()}
              columns={exportColumns}
              fileName={title}
            />
          )}
          {canCreate && (
            <Button size="sm" onClick={handleOpenCreate} className="gap-1.5">
              <PlusCircle className="h-3.5 w-3.5" />
              Crear
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <AnimatePresence>
        {showFilters && resolvedFilterFields.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <Card className="bg-gray-50/50 border-dashed">
              <CardContent className="p-3">
                <div className="flex flex-wrap gap-2">
                  {resolvedFilterFields.map((field) => (
                    <div key={field.id} className="flex items-center gap-1.5">
                      <span className="text-xs text-gray-500">{field.label}:</span>
                      {field.type === 'select' ? (
                        <select
                          value={filters[field.id] || ''}
                          onChange={(e) => handleFilterChange(field.id, e.target.value)}
                          className="text-xs h-8 rounded border border-gray-200 px-2 bg-white"
                        >
                          <option value="">Todos</option>
                          {field.options?.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      ) : (
                        <Input
                          type="text"
                          value={filters[field.id] || ''}
                          onChange={(e) => handleFilterChange(field.id, e.target.value)}
                          className="text-xs h-8 w-40"
                          placeholder={field.placeholder}
                        />
                      )}
                      {filters[field.id] && (
                        <button
                          onClick={() => handleRemoveFilter(field.id)}
                          className="text-xs text-red-500 hover:text-red-700"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Table */}
      <div className="border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b">
                {resolvedColumns.map((col) => (
                  <th key={col.id} className="text-left px-3 py-2.5 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    {col.label}
                  </th>
                ))}
                {showActions && (
                  <th className="text-right px-3 py-2.5 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Acciones
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={resolvedColumns.length + (showActions ? 1 : 0)} className="px-3 py-8 text-center">
                    <Loader2 className="h-6 w-6 animate-spin mx-auto text-gray-400" />
                    <p className="text-xs text-gray-400 mt-2">Cargando...</p>
                  </td>
                </tr>
              ) : data.length === 0 ? (
                <tr>
                  <td colSpan={resolvedColumns.length + (showActions ? 1 : 0)} className="px-3 py-8 text-center text-gray-400 text-sm">
                    No se encontraron registros
                  </td>
                </tr>
              ) : (
                data.map((row, idx) => (
                  <tr key={row._id || idx} className="border-b hover:bg-gray-50/50 transition-colors">
                    {resolvedColumns.map((col) => (
                      <td key={col.id} className="px-3 py-2.5">
                        {renderCell(row, col)}
                      </td>
                    ))}
                    {showActions && (
                      <td className="px-3 py-2.5 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => (onView ? onView(row) : handleOpenDetail(row))}
                            title="Ver detalle"
                          >
                            <Eye className="h-3.5 w-3.5 text-indigo-600" />
                          </Button>
                          {extraActions.map((action, idx) => (
                            <Button
                              key={idx}
                              variant="ghost"
                              size="icon"
                              className={`h-7 w-7 ${action.className || ''}`}
                              onClick={() => action.onClick(row)}
                              title={action.label}
                            >
                              <action.icon className="h-3.5 w-3.5" />
                            </Button>
                          ))}
                          {canEdit && (
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleOpenEdit(row)} title="Editar">
                              <Pencil className="h-3.5 w-3.5 text-blue-600" />
                            </Button>
                          )}
                          {canDelete && (
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleOpenDelete(row)} title="Eliminar">
                              <Trash2 className="h-3.5 w-3.5 text-red-600" />
                            </Button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-gray-500">
            Página {currentPage} de {totalPages} ({totalItems} registros)
          </p>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1 || isLoading}
              className="h-8 w-8 p-0"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              return (
                <Button
                  key={pageNum}
                  variant={currentPage === pageNum ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setCurrentPage(pageNum)}
                  disabled={isLoading}
                  className="h-8 w-8 p-0 text-xs"
                >
                  {pageNum}
                </Button>
              );
            })}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages || isLoading}
              className="h-8 w-8 p-0"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <DynamicFormModal
            isOpen={isModalOpen}
            onClose={() => { setIsModalOpen(false); setCurrentItem(null); }}
            onSave={handleSave}
            item={currentItem}
            fields={resolvedFormFields}
            title={modalMode === 'create' ? `Crear ${title}` : `Editar ${title}`}
            mode={modalMode}
          />
        )}
      </AnimatePresence>

      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Estás seguro de eliminar este registro?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Esto eliminará permanentemente el registro
              &quot;{itemToDelete?.nombre || itemToDelete?.id || ''}&quot; de tus registros.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setItemToDelete(null)}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete} className="bg-destructive hover:bg-destructive/90">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* View Detail Modal */}
      <AnimatePresence>
        {isDetailOpen && detailItem && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setIsDetailOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 300, damping: 25 }}
              className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[85vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-5 border-b bg-gray-50 rounded-t-xl">
                <div className="flex items-center gap-2">
                  <FileSpreadsheet className="h-5 w-5 text-indigo-600" />
                  <h2 className="text-lg font-bold text-gray-900">Detalle del Registro</h2>
                </div>
                <Button variant="ghost" size="icon" onClick={() => setIsDetailOpen(false)} className="h-8 w-8">
                  <X className="h-5 w-5" />
                </Button>
              </div>
              <div className="p-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1">
                  {Object.entries(detailItem)
                    .filter(([key]) => !key.startsWith('_'))
                    .map(([key, value]) => (
                      <div key={key} className="flex flex-col gap-1 py-3 border-b border-gray-100">
                        <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                          {key}
                        </span>
                        <span className="text-sm text-gray-900 break-words font-mono bg-gray-50 rounded p-1.5">
                          {typeof value === 'object' && value !== null
                            ? JSON.stringify(value, null, 2)
                            : String(value ?? '-')}
                        </span>
                      </div>
                    ))}
                </div>
              </div>
              <div className="flex justify-end p-5 border-t bg-gray-50 rounded-b-xl">
                <Button variant="outline" onClick={() => setIsDetailOpen(false)}>
                  Cerrar
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ParametrizacionInlineTable;
